import React from 'react';

import { di } from 'react-magnetic-di';
import { graphql, usePreloadedQuery } from 'react-relay';

import Placeholder from '@atlassian/react-ufo/placeholder';

import { type rootUiModificationsQuery as RootUiModificationsQueryType } from './__generated__/rootUiModificationsQuery.graphql';
import { UiModificationsRootFragment } from './fragment';
import type { RootProps, UiModificationsRootProps } from './types';

/**
 * UI modifications root component with usePreloadedQuery.
 *
 * Use it to attach to router or event if there is no possibility to use UiModificationsRootFragment.
 * In most cases we should use UiModificationsRootFragment.
 */
export const UiModificationsRoot = (props: UiModificationsRootProps) => {
	const { queryReference } = props;

	if (!queryReference) {
		return null;
	}

	return (
		<Placeholder name="ui-modifications-root-query" fallback={null}>
			<RootQuery {...props} queryReference={queryReference} />
		</Placeholder>
	);
};

const RootQuery = (props: RootProps) => {
	di(UiModificationsRootFragment);

	const { queryReference, ...restProps } = props;

	const data = usePreloadedQuery<RootUiModificationsQueryType>(
		graphql`
			query rootUiModificationsQuery(
				$cloudId: ID!
				$forgeExtensionRenderingContext: JiraExtensionRenderingContextInput!
				$uiModificationsContext: JiraUiModificationsContextInput!
				$includeEgressOverridesForge: Boolean!
			) {
				...fragment_AtlassianUiModificationsCore_RootFragment
					@arguments(
						cloudId: $cloudId
						forgeExtensionRenderingContext: $forgeExtensionRenderingContext
						uiModificationsContext: $uiModificationsContext
						includeEgressOverridesForge: $includeEgressOverridesForge
					)
			}
		`,
		queryReference,
	);

	return <UiModificationsRootFragment {...restProps} fragmentRef={data} />;
};
